import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ExamQuestion } from '../../types/composer';

interface ExamQuestionsPanelProps {
  questions: ExamQuestion[];
  disabled: boolean;
  onRemove: (eqId: number) => void;
  onUpdatePoints: (eqId: number, points: number) => void;
  onReorder: (order: { id: number; position: number }[]) => void;
  /** TF-405: open the read-only preview modal for a question. */
  onPreview: (questionId: number) => void;
}

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700',
};

const ExamQuestionsPanel: React.FC<ExamQuestionsPanelProps> = ({
  questions,
  disabled,
  onRemove,
  onUpdatePoints,
  onReorder,
  onPreview,
}) => {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(questions, oldIndex, newIndex);
    const order = reordered.map((q, index) => ({ id: q.id, position: index + 1 }));
    onReorder(order);
  };

  return (
    <div className="card p-4 bg-white rounded-lg border border-gray-200 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{t('composer.examQuestions.panelTitle')}</h3>
        <span className="text-sm text-gray-500">{t('composer.examQuestions.questionCount', { count: questions.length })}</span>
      </div>

      {questions.length === 0 ? (
        <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg">
          <div className="text-center p-8">
            <p className="text-gray-400 text-sm">
              {t('composer.examQuestions.emptyLine1')}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {t('composer.examQuestions.emptyLine2')}
            </p>
          </div>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={questions.map((q) => q.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
              {questions.map((q, index) => (
                <SortableExamQuestionItem
                  key={q.id}
                  question={q}
                  position={index + 1}
                  disabled={disabled}
                  onRemove={onRemove}
                  onUpdatePoints={onUpdatePoints}
                  onPreview={onPreview}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {questions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600 flex justify-between">
          <span>{t('composer.examQuestions.total')}</span>
          <span className="font-semibold">
            {questions.reduce((sum, q) => sum + q.points, 0)} {t('composer.examQuestions.points')}
          </span>
        </div>
      )}
    </div>
  );
};

interface SortableExamQuestionItemProps {
  question: ExamQuestion;
  position: number;
  disabled: boolean;
  onRemove: (eqId: number) => void;
  onUpdatePoints: (eqId: number, points: number) => void;
  onPreview: (questionId: number) => void;
}

const SortableExamQuestionItem: React.FC<SortableExamQuestionItemProps> = ({
  question,
  position,
  disabled,
  onRemove,
  onUpdatePoints,
  onPreview,
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
    disabled,
  });

  const [localPoints, setLocalPoints] = useState(question.points);

  useEffect(() => {
    setLocalPoints(question.points);
  }, [question.points]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-3 rounded-lg border border-gray-200 bg-white"
    >
      <div className="flex items-start gap-2">
        {/* Drag handle */}
        {!disabled && (
          <button
            {...attributes}
            {...listeners}
            className="mt-0.5 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0 focus:outline-none"
            aria-label={t('composer.examQuestions.dragHandle')}
          >
            <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
              <circle cx="4" cy="3" r="1.5" />
              <circle cx="8" cy="3" r="1.5" />
              <circle cx="4" cy="8" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="4" cy="13" r="1.5" />
              <circle cx="8" cy="13" r="1.5" />
            </svg>
          </button>
        )}

        {/* Position number */}
        <span className="text-sm font-semibold text-gray-400 flex-shrink-0 w-5 text-right">
          {position}.
        </span>

        {/* Question content */}
        <div
          className="flex-1 min-w-0"
          onDoubleClick={() => onPreview(question.question_id)}
          title={t('composer.questionPool.previewHint')}
        >
          {question.review_status !== 'approved' && (
            <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mb-1">
              &#9888; {t('composer.examQuestions.notApproved')}
            </span>
          )}
          <p className="text-sm text-gray-800 line-clamp-2">{question.question_text}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${
                DIFFICULTY_COLORS[question.difficulty] || 'bg-gray-100 text-gray-600'
              }`}
            >
              {question.difficulty}
            </span>
            <span className="text-xs text-gray-400">{question.question_type}</span>
          </div>
        </div>

        {/* Points input */}
        <input
          type="number"
          min={0}
          step={0.5}
          value={localPoints}
          disabled={disabled}
          onChange={(e) => setLocalPoints(parseFloat(e.target.value) || 0)}
          onBlur={() => {
            if (localPoints !== question.points && localPoints >= 0) {
              onUpdatePoints(question.id, localPoints);
            }
          }}
          className="w-14 text-sm text-center border border-gray-300 rounded px-1 py-0.5 disabled:bg-gray-50 disabled:text-gray-400 flex-shrink-0"
          aria-label={t('composer.examQuestions.pointsLabel')}
          title={t('composer.examQuestions.pointsLabel')}
        />
        <span className="text-xs text-gray-400 flex-shrink-0">{t('composer.examQuestions.pkt')}</span>

        {/* Preview (read-only) — always available, also for finalised exams */}
        <button
          type="button"
          onClick={() => onPreview(question.question_id)}
          aria-label={t('composer.questionPool.previewAria')}
          title={t('composer.questionPool.preview')}
          className="text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-indigo-300 rounded"
        >
          <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12s3.75-7.5 9.75-7.5 9.75 7.5 9.75 7.5-3.75 7.5-9.75 7.5S2.25 12 2.25 12z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        {/* Remove button */}
        {!disabled && (
          <button
            onClick={() => onRemove(question.id)}
            className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 text-lg leading-none"
            aria-label={t('composer.examQuestions.removeQuestion')}
          >
            &times;
          </button>
        )}
      </div>
    </div>
  );
};

export default ExamQuestionsPanel;
